import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { HeartPulse, ShieldAlert, Droplet, IdCard, Sparkles, Activity, Route as RouteIcon } from 'lucide-react';
import CommonLoader from '../CommonLoader';
import HealthEntryCard from '../health/HealthEntryCard';
import AddHealthEntrySection from '../health/AddHealthEntrySection';
import AskPreviousVisits from '../health/AskPreviousVisits';
import ReadingsPanel from '../checks/ReadingsPanel';
import HealthProgrammesPanel from '../programmes/HealthProgrammesPanel';
import { formatDay } from '../health/healthFormat';
import { hospitalPatientCode } from '../../utils/patientCode';
import {
    fetchHealthRecord,
    fetchHealthReadings,
    confirmHealthEntry,
    clearHealthRecord,
    HEALTH_CATEGORIES,
} from '../../redux/healthRecordSlice';

// Check readings (check sets, Round A) are NOT an eighth category: a separate
// "Readings" chip after the seven, with its own endpoint and panel.
const READINGS = 'readings';
// Care programmes (Round B): a "Programmes" chip after Readings, badge =
// `counts.programmes` (ACTIVE ones), opening the read-only timeline section.
const PROGRAMMES = 'programmes';

// THE PATIENT'S HEALTH RECORD, seen from the hospital (doc/one-patient-row.md
// §8, Phase 3). Seven sections of a person's medical life, kept ONCE on the
// person and read at every hospital that treats them — this tab is a window
// onto it, not a copy of it.
//
// WHO SEES WHAT is the server's answer, never this component's:
//   · a doctor who has had an appointment with this person — the record, plus
//     the optional Verify tick on the lines the patient reported themselves
//     (`viewer.canWrite`, which for a doctor means exactly "linked doctor");
//   · an isolated clinic's admin — the record, read-only (`canWrite:false`);
//   · everybody else, including a connected hospital's admin and the front
//     desk — 403 NOT_LINKED_DOCTOR.
// That last one is NOT an error. It is the normal state of a patient this
// doctor has not treated yet, so it renders as a calm explanation rather than
// a red failure: the difference is the whole reason the slice keeps `code`.
//
// On the patient's tab nothing here writes to the record except Verify.
// ADDING needs an appointment: a doctor writes on somebody's permanent record
// only from inside their own appointment with them.
//
// INSIDE A VISIT (round 9) the same component is the body of the "Patient
// health record" drawer — the same header, chips and cards, not a fork — and
// `appointmentId` switches on what only makes sense there:
//   · the open visit is sent on the read, so the server can mark each entry
//     `isNew` (added since this doctor's previous visit) — a "New since last
//     visit" filter chip and a "New" badge surface them;
//   · "Add entry" appears, sending that appointment;
//   · the record is NOT cleared on close: the visit screen's compact row owns
//     it (and its "N entries this visit" count).
//
// READINGS (check sets): a chip after the seven sections, badge =
// `counts.readings` from the record's own answer, opening ReadingsPanel (its
// own endpoint, grouped per visit, number fields as a trend). In a visit the
// readings are read up front when there are any, so "New since last visit"
// counts and lists the new readings too.

function HealthRecordTab({ patientId, id, appointmentId = null }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { patient, categories, viewer, loading, error, saving, counts, readings, programmes } = useSelector((state) => state.healthRecord);

    const [activeCategory, setActiveCategory] = useState(HEALTH_CATEGORIES[0]);
    // "New since last visit" — a filter across all seven sections.
    const [showNew, setShowNew] = useState(false);
    const inVisit = Boolean(appointmentId);

    useEffect(() => {
        if (patientId) dispatch(fetchHealthRecord({ patientId, appointmentId }));
        // The record belongs to the PERSON, so a tab left mounted while the
        // page moves to another patient would show the wrong history. Inside
        // a visit the visit row clears it instead (see header).
        if (appointmentId) return undefined;
        return () => { dispatch(clearHealthRecord()); };
    }, [dispatch, patientId, appointmentId]);

    // Verify belongs to a linked doctor; an admin and the desk get nothing.
    // Both flags are required: `canWrite` is false for an isolated clinic's
    // admin, and the confirm endpoint refuses anyone who is not linked.
    const canAct = Boolean(viewer?.canWrite && viewer?.isLinkedDoctor);

    const newEntries = useMemo(
        () => HEALTH_CATEGORIES.flatMap((c) => (categories?.[c] || []).filter((e) => e.isNew === true)),
        [categories]
    );
    const readingsMine = readings?.patientId === patientId;
    const readingsCount = readingsMine && readings.loaded && !readings.error ? readings.count : (counts?.readings || 0);
    const newReadings = useMemo(
        () => (readingsMine ? (readings.visits || []).reduce((n, v) => n + (v.readings || []).filter((r) => r.isNew === true).length, 0) : 0),
        [readingsMine, readings]
    );
    // In a visit, read the readings up front when the record says there are
    // some — the "New since last visit" filter needs them.
    const recordLoaded = Boolean(patient) && !loading && !error;
    useEffect(() => {
        if (!inVisit || !patientId || !recordLoaded || !(counts?.readings > 0)) return;
        if (readingsMine && (readings.loading || readings.loaded)) return;
        dispatch(fetchHealthReadings({ patientId, appointmentId }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inVisit, patientId, appointmentId, recordLoaded, counts?.readings, dispatch]);
    const filterNew = inVisit && showNew;
    const showReadings = !filterNew && activeCategory === READINGS;
    const showProgrammes = !filterNew && activeCategory === PROGRAMMES;
    const programmesMine = programmes?.patientId === patientId;
    const programmesCount = programmesMine && programmes.loaded && !programmes.error
        ? (programmes.active || []).length
        : (counts?.programmes || 0);
    const entries = useMemo(
        () => (filterNew ? newEntries : ((activeCategory === READINGS || activeCategory === PROGRAMMES) ? [] : (categories?.[activeCategory] || []))),
        [filterNew, newEntries, categories, activeCategory]
    );
    const newCount = newEntries.length + newReadings;
    const lastVisit = formatDay(viewer?.lastVisitAt);
    const retry = () => dispatch(fetchHealthRecord({ patientId, appointmentId }));

    if (loading) {
        return (
            <div id={id} data-testid="health-record-loading" className="flex flex-col items-center justify-center py-12">
                <CommonLoader />
                <span className="text-sm text-[#4ABDBD]">{t('healthRecord.loading')}</span>
            </div>
        );
    }

    if (error?.code === 'NOT_LINKED_DOCTOR') {
        return (
            <div id={id} data-testid="health-record-not-linked" className="mx-auto max-w-xl rounded-2xl border border-[#C7D6D5] bg-[#F5FAFA] p-8 text-center">
                <ShieldAlert className="mx-auto mb-3 h-10 w-10 text-[#9DB3B1]" aria-hidden="true" />
                <h3 className="text-base font-bold text-[#2D8A8A]">{t('healthRecord.not_linked_title')}</h3>
                <p className="mt-2 text-sm text-gray-600">{t('healthRecord.not_linked_body')}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div id={id} data-testid="health-record-error" className="mx-auto max-w-xl rounded-2xl border border-red-100 bg-red-50 p-8 text-center">
                <p className="text-sm text-red-700">{t('healthRecord.load_failed')}</p>
                <button
                    type="button"
                    data-testid="health-record-retry"
                    onClick={retry}
                    className="mt-3 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] px-4 py-2 text-sm font-semibold text-white shadow-lg transition-all hover:shadow-xl"
                >
                    {t('healthRecord.retry')}
                </button>
            </div>
        );
    }

    return (
        <div id={id} data-testid="health-record-tab" className="flex flex-col gap-4">
            {/* Header — whose record this is, and the two identifiers a desk
                reads out: blood group and the hospital's patient code. */}
            <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-gray-100 bg-gradient-to-r from-[#4ABDBD]/10 to-transparent p-4">
                <HeartPulse className="h-6 w-6 text-[#2D8A8A]" aria-hidden="true" />
                <div className="min-w-0">
                    <h3 data-testid="health-record-heading" className="text-base font-bold text-gray-800">{t('healthRecord.heading')}</h3>
                    {patient?.name && <p data-testid="health-record-person" className="text-xs text-gray-500">{patient.name}</p>}
                </div>
                <div className="ml-auto flex flex-wrap items-center gap-2">
                    {/* Visit sharing v2: a linked doctor may ask this person to
                        share visits from OTHER hospitals. Here in the header,
                        so it is the same on the tab and in the visit drawer
                        (whose body is this component). */}
                    {viewer?.isLinkedDoctor && (patient?.patientId || patientId) && (
                        <AskPreviousVisits patientId={patient?.patientId || patientId} patientName={patient?.name} inVisit={inVisit} />
                    )}
                    <span data-testid="health-record-blood-group" className="inline-flex items-center gap-1 rounded-lg border border-[#4ABDBD]/30 bg-white px-2 py-1 text-xs font-semibold text-[#2D8A8A]">
                        <Droplet className="h-3.5 w-3.5" aria-hidden="true" />
                        {t('healthRecord.blood_group')}: {patient?.bloodGroup || t('healthRecord.blood_group_unknown')}
                    </span>
                    {/* The one number a desk reads out is the hospital's own
                        patient code — never the Mitra One Care / system id.
                        No code, no chip. */}
                    {hospitalPatientCode(patient) && (
                        <span data-testid="health-record-patient-code" title={t('patientprofiletab.hospital_patient_code')} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1 font-mono text-xs text-gray-600">
                            <IdCard className="h-3.5 w-3.5" aria-hidden="true" />
                            {hospitalPatientCode(patient)}
                        </span>
                    )}
                </div>
            </div>

            {!canAct && (
                <p data-testid="health-record-read-only" className="text-xs text-gray-500">{t('healthRecord.read_only_note')}</p>
            )}

            {inVisit && (
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        data-testid="health-record-filter-new"
                        aria-pressed={filterNew}
                        disabled={newCount === 0}
                        onClick={() => setShowNew((v) => !v)}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${filterNew
                            ? 'border-[#2D8A8A] bg-[#2D8A8A] text-white'
                            : 'border-[#4ABDBD]/40 bg-[#4ABDBD]/10 text-[#2D8A8A] hover:bg-[#4ABDBD]/20'}`}
                    >
                        <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                        {t('healthRecord.drawer.new_filter', 'New since last visit')}
                        <span data-testid="health-record-filter-new-count" className={`rounded-full px-1.5 py-px text-[10px] font-bold ${filterNew ? 'bg-white text-[#2D8A8A]' : 'bg-[#4ABDBD] text-white'}`}>
                            {newCount}
                        </span>
                    </button>
                    <span data-testid="health-record-last-visit" className="text-[11px] text-gray-500">
                        {lastVisit
                            ? t('healthRecord.drawer.last_visit', 'Your last visit: {{date}}', { date: lastVisit })
                            : t('healthRecord.drawer.first_visit', 'First visit with you — nothing to compare against yet.')}
                    </span>
                </div>
            )}

            {inVisit && canAct && (
                <AddHealthEntrySection patientId={patientId} appointmentId={appointmentId} />
            )}

            {/* The seven sections, as segmented tabs. Every one is rendered
                even when empty: the sections are the record's vocabulary, and
                hiding "Allergies" because none are listed would read as "no
                allergies" rather than "nothing written down". */}
            <div data-testid="health-record-sections" className="flex flex-wrap gap-2 rounded-2xl border border-gray-200 bg-gradient-to-r from-gray-50 to-[#4ABDBD]/5 p-1">
                {HEALTH_CATEGORIES.map((c) => {
                    const count = (categories?.[c] || []).length;
                    const active = !filterNew && c === activeCategory;
                    return (
                        <button
                            key={c}
                            type="button"
                            data-testid={`health-record-section-${c}`}
                            aria-pressed={active}
                            onClick={() => { setShowNew(false); setActiveCategory(c); }}
                            className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${active
                                ? 'bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow'
                                : 'text-gray-600 hover:text-[#4ABDBD]'}`}
                        >
                            {t(`healthRecord.categories.${c}`)}
                            {count > 0 && (
                                <span className={`ml-2 rounded-full px-1.5 py-px text-[10px] font-bold ${active ? 'bg-white text-[#2D8A8A]' : 'bg-[#4ABDBD] text-white'}`}>
                                    {count}
                                </span>
                            )}
                        </button>
                    );
                })}
                <button
                    type="button"
                    data-testid="health-record-section-readings"
                    aria-pressed={showReadings}
                    onClick={() => { setShowNew(false); setActiveCategory(READINGS); }}
                    className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${showReadings
                        ? 'bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow'
                        : 'text-gray-600 hover:text-[#4ABDBD]'}`}
                >
                    <Activity className="h-4 w-4" aria-hidden="true" />
                    {t('checkSets.readings_chip')}
                    {readingsCount > 0 && (
                        <span data-testid="health-record-readings-count" className={`ml-1 rounded-full px-1.5 py-px text-[10px] font-bold ${showReadings ? 'bg-white text-[#2D8A8A]' : 'bg-[#4ABDBD] text-white'}`}>
                            {readingsCount}
                        </span>
                    )}
                </button>
                <button
                    type="button"
                    data-testid="health-record-section-programmes"
                    aria-pressed={showProgrammes}
                    onClick={() => { setShowNew(false); setActiveCategory(PROGRAMMES); }}
                    className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${showProgrammes
                        ? 'bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow'
                        : 'text-gray-600 hover:text-[#4ABDBD]'}`}
                >
                    <RouteIcon className="h-4 w-4" aria-hidden="true" />
                    {t('programmes.record_chip')}
                    {programmesCount > 0 && (
                        <span data-testid="health-record-programmes-count" className={`ml-1 rounded-full px-1.5 py-px text-[10px] font-bold ${showProgrammes ? 'bg-white text-[#2D8A8A]' : 'bg-[#4ABDBD] text-white'}`}>
                            {programmesCount}
                        </span>
                    )}
                </button>
            </div>

            {showProgrammes ? (
                <HealthProgrammesPanel patientId={patientId} />
            ) : showReadings ? (
                <ReadingsPanel patientId={patientId} appointmentId={appointmentId} />
            ) : filterNew && newEntries.length === 0 && newReadings > 0 ? (
                <ReadingsPanel patientId={patientId} appointmentId={appointmentId} onlyNew />
            ) : entries.length === 0 ? (
                <p data-testid={`health-record-empty-${filterNew ? 'new' : activeCategory}`} className="rounded-2xl border border-dashed border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
                    {filterNew ? t('healthRecord.drawer.new_empty', 'Nothing new since your last visit.') : t(`healthRecord.empty.${activeCategory}`)}
                </p>
            ) : (
                <ul data-testid={`health-record-timeline-${filterNew ? 'new' : activeCategory}`} className="flex flex-col gap-3">
                    {entries.map((entry) => (
                        <HealthEntryCard
                            key={entry.entryId}
                            entry={entry}
                            canAct={canAct}
                            busy={saving}
                            onVerify={(e) => dispatch(confirmHealthEntry({ entryId: e.entryId }))}
                        />
                    ))}
                </ul>
            )}
            {/* "New since last visit" lists the new readings under the new
                entries (the case of only new readings is handled above). */}
            {filterNew && newEntries.length > 0 && newReadings > 0 && (
                <ReadingsPanel patientId={patientId} appointmentId={appointmentId} onlyNew />
            )}
        </div>
    );
}

export default HealthRecordTab;
